/**
 * SKUNoteModal - Modal for adding notes to specific SKUs
 *
 * Features:
 * - Pre-filled template with Project/SKU/Issue
 * - Option to send to PM
 * - Shows existing notes for the SKU
 * - Immutable note lifecycle
 */

import { formatTimeAgo } from './projectUtils';

export default function SKUNoteModal({
  isOpen,
  sku,
  noteText,
  onNoteTextChange,
  sendToPM,
  onSendToPMChange,
  existingNotes,
  onSave,
  onClose,
  saving,
}) {
  if (!isOpen || !sku) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-gray-300">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-lg mx-4">
        {/* Modal Header */}
        <div className="px-4 py-3 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">
            Note for SKU: {sku.sku}
          </h3>
          <p className="text-sm text-gray-500 truncate">
            {sku.desc || sku.description}
          </p>
        </div>

        {/* Modal Body */}
        <div className="px-4 py-4 space-y-4">
          {/* Note textarea with pre-filled template */}
          <textarea
            value={noteText}
            onChange={(e) => onNoteTextChange(e.target.value)}
            rows={5}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent resize-none font-mono"
            placeholder="Describe the issue..."
          />

          {/* Send to PM checkbox */}
          <label className="flex items-center gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={sendToPM}
              onChange={(e) => onSendToPMChange(e.target.checked)}
              className="w-4 h-4 text-purple-600 rounded"
            />
            <span className="text-sm text-gray-700">Send to PM (creates notification)</span>
          </label>

          {/* Existing notes for this SKU */}
          {existingNotes?.length > 0 && (
            <div className="border-t border-gray-200 pt-4">
              <p className="text-xs font-medium text-gray-500 mb-2">Previous notes for this SKU:</p>
              <div className="space-y-2 max-h-32 overflow-y-auto">
                {existingNotes.map((note) => (
                  <div key={note.id} className="p-2 bg-slate-200 rounded text-xs">
                    <div className="flex justify-between text-gray-500 mb-1">
                      <span>{note.author_name}</span>
                      <span>{formatTimeAgo(note.created_at)}</span>
                    </div>
                    <p className="text-gray-700 whitespace-pre-wrap">{note.content}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Note lifecycle info */}
          <p className="text-xs text-gray-500 italic">
            Notes are immutable records. Corrections require a new note.
          </p>
        </div>

        {/* Modal Footer */}
        <div className="px-4 py-3 border-t border-gray-200 flex justify-end gap-2">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 hover:bg-slate-100 rounded-lg transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={onSave}
            disabled={!noteText.trim() || saving}
            className="px-4 py-2 text-sm font-medium text-gray-900 bg-purple-600 hover:bg-purple-700 disabled:bg-gray-300 disabled:cursor-not-allowed rounded-lg transition-colors"
          >
            {saving ? 'Saving...' : 'Save Note'}
          </button>
        </div>
      </div>
    </div>
  );
}
